/**
 * CategoryDrilldownModal Component
 * 
 * Deep-dive into cost per episode and utilization rate for a specific service category.
 * Shows comparison between provider and national benchmarks.
 * 
 * @see MIPS_Cost_Dashboard_Measure_Detail_Page_Specification_v1.0, Section 4.1
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, AlertTriangle, Check, Info } from 'lucide-react';
import { DriverBadge } from './CostSharedComponents';

// ============================================
// Types
// ============================================

export interface CategoryData {
  measureId: string;
  category: string;
  qscGroup: string;
  providerAvgCost: number;
  nationalAvgCost: number;
  episodesWithService: number;
  providerPctEpisodes: number;
  nationalPctEpisodes: number;
  costVariance: number;
  utilVariance: number;
  driverType: 'STRENGTH' | 'COST' | 'UTILIZATION' | 'BOTH';
  lowVolume?: boolean;
  opportunity: number;
}

interface CategoryDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: CategoryData | null;
  measureName?: string;
}

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// ============================================
// Component
// ============================================

export function CategoryDrilldownModal({
  isOpen,
  onClose,
  category,
  measureName,
}: CategoryDrilldownModalProps) {
  if (!category) return null;

  // Verdicts derive from the backend driverType (single source of truth with
  // the table badge — includes materiality thresholds), not raw comparisons
  const costDiff = category.providerAvgCost - category.nationalAvgCost;
  const isIntensityIssue = category.driverType === 'COST' || category.driverType === 'BOTH';

  const utilMultiplier = category.nationalPctEpisodes > 0
    ? category.providerPctEpisodes / category.nationalPctEpisodes
    : 1;
  const isUtilIssue = category.driverType === 'UTILIZATION' || category.driverType === 'BOTH';
  const utilDiffPp = category.providerPctEpisodes - category.nationalPctEpisodes;

  // Calculate bar widths (relative to max)
  const maxCost = Math.max(category.providerAvgCost, category.nationalAvgCost);
  const providerCostWidth = maxCost > 0 ? (category.providerAvgCost / maxCost) * 100 : 0;
  const nationalCostWidth = maxCost > 0 ? (category.nationalAvgCost / maxCost) * 100 : 0;

  const maxUtil = Math.max(category.providerPctEpisodes, category.nationalPctEpisodes, 100);
  const providerUtilWidth = (category.providerPctEpisodes / maxUtil) * 100;
  const nationalUtilWidth = (category.nationalPctEpisodes / maxUtil) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl [&>button.absolute]:hidden">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold">
                {category.category}
              </DialogTitle>
              {measureName && (
                <p className="text-sm text-muted-foreground mt-1">
                  {measureName}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <DriverBadge type={category.lowVolume ? 'LOW_VOLUME' : category.driverType} size="md" />
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 mt-4">
          {/* Spend Per Episode (intensity) */}
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">
              Spend Per Episode (This Category)
            </h4>

            {/* Provider Cost Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Your Average</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(category.providerAvgCost)}
                </span>
              </div>
              <div className="h-6 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${providerCostWidth}%` }}
                />
              </div>
            </div>

            {/* National Cost Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">National Average</span>
                <span className="font-mono text-muted-foreground">
                  {formatCurrency(category.nationalAvgCost)}
                </span>
              </div>
              <div className="h-6 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-400 rounded-full transition-all"
                  style={{ width: `${nationalCostWidth}%` }}
                />
              </div>
            </div>

            {/* Intensity Status */}
            <div className={`rounded-lg p-3 ${!isIntensityIssue ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center gap-2">
                {!isIntensityIssue ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                )}
                <span className={`font-semibold ${!isIntensityIssue ? 'text-green-700' : 'text-amber-700'}`}>
                  {!isIntensityIssue ? 'AT OR NEAR NATIONAL' : 'HIGHER SERVICE INTENSITY'}
                </span>
              </div>
              <p className={`text-sm mt-1 ${!isIntensityIssue ? 'text-green-600' : 'text-amber-600'}`}>
                {isIntensityIssue ? (
                  <>
                    Your episodes accumulate{' '}
                    <span className="font-semibold">{formatCurrency(Math.abs(costDiff))}</span>
                    {' '}more of this service per episode than the national average — more
                    services and/or higher-level codes, not higher prices.
                  </>
                ) : costDiff <= 0 ? (
                  <>
                    Your episodes accumulate{' '}
                    <span className="font-semibold">{formatCurrency(Math.abs(costDiff))}</span>
                    {' '}less of this service per episode than the national average.
                  </>
                ) : (
                  'Spend per episode is within the normal range of the national average.'
                )}
              </p>
            </div>
          </div>

          {/* How Often Episodes Include This Category (frequency) */}
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">
              How Often Episodes Include This
            </h4>

            {/* Provider Utilization Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Your Rate</span>
                <span className="font-mono font-semibold">
                  {formatPercent(category.providerPctEpisodes)}
                </span>
              </div>
              <div className="h-6 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${providerUtilWidth}%` }}
                />
              </div>
            </div>

            {/* National Utilization Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">National</span>
                <span className="font-mono text-muted-foreground">
                  {formatPercent(category.nationalPctEpisodes)}
                </span>
              </div>
              <div className="h-6 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-400 rounded-full transition-all"
                  style={{ width: `${nationalUtilWidth}%` }}
                />
              </div>
            </div>

            {/* Frequency Status */}
            <div className={`rounded-lg p-3 ${!isUtilIssue ? 'bg-green-50 border border-green-200' : 'bg-sky-50 border border-sky-200'}`}>
              <div className="flex items-center gap-2">
                {!isUtilIssue ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-sky-600" />
                )}
                <span className={`font-semibold ${!isUtilIssue ? 'text-green-700' : 'text-sky-700'}`}>
                  {!isUtilIssue ? 'IN LINE WITH NATIONAL' : 'IN MORE OF YOUR EPISODES'}
                </span>
              </div>
              <p className={`text-sm mt-1 ${!isUtilIssue ? 'text-green-600' : 'text-sky-600'}`}>
                {!isUtilIssue ? (
                  'The share of your episodes including this service is in line with the national rate.'
                ) : (
                  <>
                    This service appears in{' '}
                    <span className="font-semibold">{utilMultiplier.toFixed(1)}x</span>
                    {' '}the share of episodes seen nationally ({utilDiffPp.toFixed(1)} percentage
                    points more of your episodes include it).
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Interpretation note */}
        <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            Medicare payment rates are standardized, so a higher spend per episode never means
            higher prices — it reflects the volume and level of {category.category} services
            accumulated within each episode.
            {category.lowVolume && (
              <>
                {' '}Note: only {category.episodesWithService} of your episodes include this
                service — comparisons at this volume are noisy and should be read with caution.
              </>
            )}
          </p>
        </div>

        {/* Opportunity Summary */}
        {category.opportunity > 0 && (
          <div className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Total Opportunity in this Category
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Based on {category.episodesWithService} episodes with this service
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-700">
                  {formatCurrency(category.opportunity)}
                </p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CategoryDrilldownModal;

